import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import {
  formatBatchSchedule,
  resolveInstructorName,
  isBatchEnded,
} from "@/lib/batchFormat";
import { getCapacityStatus, capacityLabel, capacityTextClass, capacityBadge } from "@/lib/capacity";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

/**
 * Standardised batch selector — 3-line entries:
 *   Line 1: <bold>Batch Name</bold> <light>- Style</light>
 *   Line 2: Mon / Wed / Fri • 6:00 PM–7:00 PM
 *   Line 3: Studio Name
 *
 * Mobile (≤767px): full-height bottom sheet with sticky search + scrollable list +
 *   sticky footer (Cancel · Clear).
 * Desktop (≥768px): inline popover anchored to the trigger; capped at 70vh so long
 *   lists scroll within the popover instead of pushing the dialog.
 */
export const BatchPicker = ({
  value,
  batches = [],
  styles = [],
  studios = [],
  instructors = [],
  masterInstructors = [],
  excludeBatchId,
  placeholder = "Choose Batch",
  testIdPrefix = "batch",
  /**
   * Phase-C: when true (default), inactive & archived batches are hidden — picker
   * shows only batches accepting new students. Set to false on history/audit screens.
   */
  activeOnly = true,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const popoverRef = useRef(null);
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );

  // Track viewport breakpoint changes
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Close inline popover (desktop only) on outside click
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, isMobile]);

  // Auto-focus search when sheet/popover opens
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setTimeout(() => {
      const ref = isMobile ? mobileInputRef : desktopInputRef;
      ref.current?.focus();
    }, 50);
  }, [open, isMobile]);

  const baseList = useMemo(
    () => {
      let list = batches;
      if (excludeBatchId) list = list.filter((b) => b.batch_id !== excludeBatchId);
      // Phase-C: hide inactive & archived batches unless caller opts out
      if (activeOnly) {
        list = list.filter((b) => (b.status || "active") === "active");
      }
      return list;
    },
    [batches, excludeBatchId, activeOnly],
  );

  const ranked = useMemo(() => {
    const q = query.trim().toLowerCase();
    const enrich = (b) => {
      const style = styles.find((s) => s.style_id === b.style_id);
      const studio = studios.find((s) => s.studio_id === b.studio_id);
      const schedule = formatBatchSchedule(b);
      const instructor = resolveInstructorName(b, instructors, masterInstructors);
      const cap = getCapacityStatus(b);
      return {
        b,
        style,
        studio,
        schedule,
        instructor,
        cap,
        ended: isBatchEnded(b),
        haystack: [b.name, style?.name, studio?.name, instructor, schedule, b.level]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      };
    };
    let rows = baseList.map(enrich);
    if (q) rows = rows.filter((r) => r.haystack.includes(q));
    rows.sort((a, b) => Number(a.ended) - Number(b.ended));
    return rows;
  }, [baseList, styles, studios, instructors, masterInstructors, query]);

  const selected = useMemo(() => {
    const b = baseList.find((x) => x.batch_id === value);
    if (!b) return null;
    const style = styles.find((s) => s.style_id === b.style_id);
    const studio = studios.find((s) => s.studio_id === b.studio_id);
    return {
      b,
      style,
      studio,
      schedule: formatBatchSchedule(b),
      ended: isBatchEnded(b),
      cap: getCapacityStatus(b),
    };
  }, [baseList, styles, studios, value]);

  const handlePick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  // Inline search input renderer — kept as a stable JSX block (NOT a sub-component
  // function) to preserve focus across keystrokes. Defining a function component
  // INSIDE BatchPicker's render would create a fresh component identity on every
  // setQuery → React would unmount the old <input> DOM node and mount a new one,
  // dropping focus and dismissing the mobile keyboard. (Apr 2026 fix.)
  const renderSearchBar = (inputRef) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
      <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search class, studio, instructor, day..."
        className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
        data-testid={`${testIdPrefix}-select-search`}
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery("")}
          className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  // Shared list item renderer
  const renderItem = ({ b, style, studio, schedule, ended, cap }) => {
    const isCurrent = b.batch_id === value;
    const badge = capacityBadge(cap);
    return (
      <button
        key={b.batch_id}
        type="button"
        data-testid={`${testIdPrefix}-select-option-${b.batch_id}`}
        onClick={() => handlePick(b.batch_id)}
        className={`w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition border-t border-slate-100 first:border-0 ${isCurrent ? "bg-violet-50/60" : ""} ${ended ? "opacity-70" : ""}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm leading-tight min-w-0 flex-1">
            <span className={`font-semibold ${ended ? "text-slate-500" : (isCurrent ? "text-violet-700" : "text-slate-900")}`}>
              {b.name}
            </span>
            {style && (
              <span className="font-normal text-slate-400 text-xs"> - {style.name}</span>
            )}
            {ended && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium">Ended</span>
            )}
          </div>
          {badge && (
            <span
              data-testid={`${testIdPrefix}-select-option-${b.batch_id}-capacity-badge`}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${badge.cls}`}
            >
              {badge.label}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-1 leading-tight">{schedule}</div>
        <div className="text-xs leading-tight mt-0.5 flex items-center gap-1.5 flex-wrap">
          {studio && <span className="text-slate-400">{studio.name}</span>}
          {studio && cap && <span className="text-slate-300">•</span>}
          {cap && (
            <span
              data-testid={`${testIdPrefix}-select-option-${b.batch_id}-capacity`}
              className={capacityTextClass(cap)}
            >
              {capacityLabel(cap)}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        data-testid={`${testIdPrefix}-select-trigger`}
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[3.5rem] rounded-xl border border-slate-200 bg-white px-4 py-2 text-left flex items-center justify-between hover:border-violet-300 transition gap-2"
      >
        {selected ? (
          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-sm leading-tight truncate w-full">
              <span className={`font-semibold ${selected.ended ? "text-slate-500" : "text-slate-900"}`}>
                {selected.b.name}
              </span>
              {selected.style && (
                <span className="font-normal text-slate-400 text-xs"> - {selected.style.name}</span>
              )}
              {selected.ended && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-medium">Ended</span>
              )}
              {(() => {
                const b = capacityBadge(selected.cap);
                return b ? (
                  <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${b.cls}`}>
                    {b.label}
                  </span>
                ) : null;
              })()}
            </span>
            <span className="text-[11px] text-slate-500 truncate w-full leading-tight mt-0.5">
              {selected.schedule}
            </span>
            <span className="text-[11px] truncate w-full leading-tight flex items-center gap-1">
              {selected.studio && <span className="text-slate-400">{selected.studio.name}</span>}
              {selected.studio && selected.cap && <span className="text-slate-300">•</span>}
              {selected.cap && (
                <span className={capacityTextClass(selected.cap)}>{capacityLabel(selected.cap)}</span>
              )}
            </span>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">{placeholder}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-slate-400 ml-2 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* DESKTOP — anchored inline popover (visible only when open && !isMobile) */}
      {open && !isMobile && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col max-h-[70vh] min-w-[20rem]">
          <div className="px-3 py-2 border-b border-slate-100 bg-white">
            {renderSearchBar(desktopInputRef)}
          </div>
          <div className="flex-1 overflow-y-auto">
            {!query && (
              <button
                type="button"
                onClick={() => handlePick("")}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 border-b border-slate-100"
                data-testid={`${testIdPrefix}-select-clear`}
              >
                {placeholder}
              </button>
            )}
            {ranked.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-slate-400">
                No batches match "{query}"
              </p>
            ) : (
              ranked.map(renderItem)
            )}
          </div>
        </div>
      )}

      {/* MOBILE — full-height bottom sheet */}
      <Sheet open={open && isMobile} onOpenChange={(v) => setOpen(v)}>
        <SheetContent
          side="bottom"
          className="h-[92vh] p-0 flex flex-col rounded-t-2xl"
          data-testid={`${testIdPrefix}-select-sheet`}
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-slate-100">
            <SheetTitle className="text-base font-semibold text-left">Select Batch</SheetTitle>
            <div className="pt-2">
              {renderSearchBar(mobileInputRef)}
            </div>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {ranked.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-slate-400">
                {query ? `No batches match "${query}"` : "No batches available"}
              </p>
            ) : (
              ranked.map(renderItem)
            )}
          </div>
          <div className="border-t border-slate-100 px-4 py-3 flex gap-2 bg-white">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1 rounded-full"
              data-testid={`${testIdPrefix}-select-cancel`}
            >
              Cancel
            </Button>
            {value && (
              <Button
                variant="outline"
                onClick={() => handlePick("")}
                className="flex-1 rounded-full text-slate-600"
                data-testid={`${testIdPrefix}-select-clear`}
              >
                Clear
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BatchPicker;
